/**
 * La source « session » des présences, sans la base : ce que la coordination
 * lit dans les tables avec ses droits, remis dans la forme que les écrans
 * attendent (`ListeDAppel`, `EssentielsDeLEnfant`, `FicheEnfant`).
 *
 * Les lectures et les écritures sont dans `source.session.ts` ; ici, les
 * règles — les mêmes que celles des fonctions du lien (migrations
 * `20261024230000` et `20261025100000`) : une inscription annulée ne compte
 * pas, un jour férié ne se pointe pas, un jour sans session non plus,
 * l'espace des familles décide de la photo, et l'ordre est celui des stages
 * puis des prénoms.
 */
import { JOURS, lundiDe, lundiPropose, type EnfantDuLien, type FicheEnfant, type JourDuLien, type ListeDAppel, type PresenceDeLEnfant } from './lien';
import type { EssentielsDeLEnfant } from './essentiels';
import type { CleDeReglement } from '../inscriptions/reglement';

/* ------------------------------------------------------------------------ */
/*  Les semaines d'un centre                                                 */
/* ------------------------------------------------------------------------ */

export interface SessionDuCentre {
  id: string;
  stageId: string;
  stage: string;
  debut: string;
  fin: string;
  periode: string;
  semaine: string | null;
}

export interface SemaineDuCentre {
  /** `AAAA-MM-JJ` du lundi : la clé. */
  lundi: string;
  vendredi: string;
  /** « Automne S2 », ou « Automne S2 / Toussaint » si deux périodes se chevauchent. */
  libelle: string;
  sessions: SessionDuCentre[];
}

const plusJours = (iso: string, n: number): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

/** Les semaines (du lundi au vendredi) où le centre a au moins une session, dans l'ordre. */
export function semainesDuCentre(sessions: readonly SessionDuCentre[]): SemaineDuCentre[] {
  const parLundi = new Map<string, SessionDuCentre[]>();
  for (const s of sessions) {
    // Une session de plusieurs semaines compte dans chacune.
    for (let lundi = lundiDe(s.debut); lundi <= s.fin; lundi = plusJours(lundi, 7)) {
      parLundi.set(lundi, [...(parLundi.get(lundi) ?? []), s]);
    }
  }
  return [...parLundi.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([lundi, ss]) => ({
      lundi,
      vendredi: plusJours(lundi, 4),
      libelle: [...new Set(ss.map((s) => `${s.periode}${s.semaine ? ` ${s.semaine}` : ''}`))].join(' / '),
      sessions: ss,
    }));
}

/**
 * La semaine à ouvrir : celle en cours du lundi au vendredi, la prochaine le
 * week-end (le dimanche, on prépare lundi), sinon la prochaine qui existe,
 * sinon la dernière.
 */
export function semaineProposee(semaines: readonly SemaineDuCentre[], aujourdHui: string): SemaineDuCentre | null {
  if (semaines.length === 0) return null;
  const lundi = lundiPropose(aujourdHui);
  return semaines.find((s) => s.lundi === lundi) ?? semaines.find((s) => s.lundi > lundi) ?? semaines[semaines.length - 1];
}

/** La semaine que désigne une tuile du tableau de bord (période, semaine, année). */
export function semaineDesParametres(
  semaines: readonly SemaineDuCentre[],
  p: { periode?: string; semaine?: string; year?: string },
): SemaineDuCentre | null {
  if (!p.periode) return null;
  return (
    semaines.find((s) =>
      s.sessions.some(
        (x) =>
          x.periode === p.periode &&
          (p.semaine ? x.semaine === p.semaine : true) &&
          (p.year ? x.debut.startsWith(p.year) : true),
      ),
    ) ?? null
  );
}

/* ------------------------------------------------------------------------ */
/*  La liste                                                                 */
/* ------------------------------------------------------------------------ */

export interface InscriptionLue {
  id: string;
  sessionId: string;
  kidId: string;
  userId: string | null;
  prenom: string;
  nom: string;
  photo: string | null;
  sousGroupe: string | null;
  cancellationStatus: string | null;
}

export interface PresenceLue {
  registration_id: string;
  monday_present: boolean | null;
  tuesday_present: boolean | null;
  wednesday_present: boolean | null;
  thursday_present: boolean | null;
  friday_present: boolean | null;
  notes: string | null;
}

/** Une inscription vivante : ni annulée, ni en cours d'annulation aboutie. */
export function estVivante(cancellationStatus: string | null | undefined): boolean {
  return !(cancellationStatus ?? 'none').startsWith('cancelled');
}

/** La présence telle que les écrans la lisent, depuis une ligne de `presences`. */
export function presenceDepuisLaLigne(p: PresenceLue): PresenceDeLEnfant {
  return {
    monday: p.monday_present,
    tuesday: p.tuesday_present,
    wednesday: p.wednesday_present,
    thursday: p.thursday_present,
    friday: p.friday_present,
    notes: p.notes?.trim() ? p.notes.trim() : null,
  };
}

export function assemblerListe(args: {
  centre: { id: string; nom: string };
  semaine: SemaineDuCentre;
  inscriptions: readonly InscriptionLue[];
  presences: readonly PresenceLue[];
  feries: readonly { date: string; name: string }[];
}): ListeDAppel {
  const { centre, semaine, inscriptions, presences, feries } = args;
  const stageDe = new Map(semaine.sessions.map((s) => [s.id, s]));
  const presenceDe = new Map(presences.map((p) => [p.registration_id, presenceDepuisLaLigne(p)]));

  const jours: JourDuLien[] = JOURS.map((cle, i) => {
    const date = plusJours(semaine.lundi, i);
    return {
      cle,
      date,
      ferie: feries.find((f) => f.date === date)?.name ?? null,
      sansSession: !semaine.sessions.some((s) => s.debut <= date && s.fin >= date),
    };
  });

  const enfants: EnfantDuLien[] = inscriptions
    .filter((i) => estVivante(i.cancellationStatus) && stageDe.has(i.sessionId))
    .map((i) => ({
      registrationId: i.id,
      prenom: i.prenom,
      nom: i.nom,
      stage: stageDe.get(i.sessionId)?.stage ?? '',
      sousGroupe: i.sousGroupe,
      presence: presenceDe.get(i.id) ?? null,
    }))
    .sort((a, b) => a.stage.localeCompare(b.stage, 'fr') || a.prenom.localeCompare(b.prenom, 'fr') || a.nom.localeCompare(b.nom, 'fr'));

  return {
    centre,
    semaine: { debut: semaine.lundi, fin: semaine.vendredi, libelle: semaine.libelle },
    jours,
    enfants,
  };
}

/* ------------------------------------------------------------------------ */
/*  Les essentiels et la fiche                                               */
/* ------------------------------------------------------------------------ */

export interface AllergiesLues { kid_id: string; has_allergies: boolean | null; allergies_details: string | null; allergies_consequences: string | null; special_diet: boolean | null; diet_details: string | null }
export interface SanteLue { kid_id: string; medication: boolean | null; medication_details: string | null; medication_autonomy: boolean | null; specific_medical: string | null; past_medical: string | null; tetanus: boolean | null; doctor_name: string | null; doctor_phone: string | null }
export interface DepartLu { kid_id: string; leaves_alone: boolean | null; departure_time: string | null; pickup_people_ids: string[] | null }
export interface PersonneLue { id: string; first_name: string | null; last_name: string | null; phone_number: string | null; relationship: string | null }
export interface ImageLue { kid_id: string; internal_spaces: boolean | null; activity_reports: boolean | null; public_website: boolean | null; social_media: boolean | null; print_materials: boolean | null; consented_at: string | null }
export interface ActivitesLues { kid_id: string; can_participate: boolean | null; restriction_details: string | null; can_swim: boolean | null; swim_level: string | null; water_fear: boolean | null; other_info: string | null }
export interface InclusionLue { kid_id: string; has_needs: boolean | null; situation_details: string | null; impact_details: string | null; needs_dedicated_staff: string | null; staff_details: string | null; strategies: string | null; assistive_devices: string | null; stress_signals: string | null; strengths: string | null; previous_experience: string | null }

const texte = (v: string | null | undefined): string | null => (v && v.trim() ? v.trim() : null);

export interface TablesDesEnfants {
  allergies: readonly AllergiesLues[];
  sante: readonly SanteLue[];
  departs: readonly DepartLu[];
  personnes: readonly PersonneLue[];
  images: readonly ImageLue[];
}

/** Les personnes autorisées d'un départ, dans l'ordre de leur nom. */
function personnesDuDepart(d: DepartLu | undefined, personnes: readonly PersonneLue[]) {
  const ids = new Set(d?.pickup_people_ids ?? []);
  return personnes
    .filter((p) => ids.has(p.id))
    .sort((a, b) => (a.last_name ?? '').localeCompare(b.last_name ?? '', 'fr') || (a.first_name ?? '').localeCompare(b.first_name ?? '', 'fr'));
}

export function assemblerEssentiels(
  inscriptions: readonly InscriptionLue[],
  t: TablesDesEnfants,
): Map<string, EssentielsDeLEnfant> {
  const allergiesDe = new Map(t.allergies.map((a) => [a.kid_id, a]));
  const santeDe = new Map(t.sante.map((s) => [s.kid_id, s]));
  const departDe = new Map(t.departs.map((d) => [d.kid_id, d]));
  const imageDe = new Map(t.images.map((i) => [i.kid_id, i]));
  const carte = new Map<string, EssentielsDeLEnfant>();
  for (const i of inscriptions) {
    const a = allergiesDe.get(i.kidId);
    const s = santeDe.get(i.kidId);
    const d = departDe.get(i.kidId);
    carte.set(i.id, {
      registrationId: i.id,
      photo: texte(i.photo),
      imageOk: imageDe.get(i.kidId)?.internal_spaces === true,
      sante: {
        allergies: a?.has_allergies ?? null,
        allergiesDetails: texte(a?.allergies_details),
        allergiesConsequences: texte(a?.allergies_consequences),
        regime: a?.special_diet ?? null,
        regimeDetails: texte(a?.diet_details),
        medicaments: s?.medication ?? null,
        medicamentsDetails: texte(s?.medication_details),
        medicamentsAutonomie: s?.medication_autonomy ?? null,
        aSavoir: texte(s?.specific_medical),
      },
      depart: {
        partSeul: d?.leaves_alone ?? null,
        heure: texte(d?.departure_time),
        personnes: personnesDuDepart(d, t.personnes).map((p) => ({ prenom: p.first_name ?? '', nom: p.last_name ?? '' })),
      },
    });
  }
  return carte;
}

export function assemblerFiche(args: {
  inscription: InscriptionLue;
  stage: string;
  enfant: { date_naissance: string | null; ecole: string | null };
  ecole: string | null;
  parent: { prenom: string | null; nom: string | null; telephone: string | null; telephone2: string | null } | null;
  tables: TablesDesEnfants;
  activites: ActivitesLues | null;
  inclusion: InclusionLue | null;
}): FicheEnfant {
  const { inscription: i, tables: t } = args;
  const a = t.allergies.find((x) => x.kid_id === i.kidId);
  const s = t.sante.find((x) => x.kid_id === i.kidId);
  const d = t.departs.find((x) => x.kid_id === i.kidId);
  const img = t.images.find((x) => x.kid_id === i.kidId);
  const ac = args.activites;
  const inc = args.inclusion;
  return {
    registrationId: i.id,
    stage: args.stage,
    sousGroupe: i.sousGroupe,
    prenom: i.prenom,
    nom: i.nom,
    dateNaissance: texte(args.enfant.date_naissance),
    ecole: texte(args.ecole),
    parent: {
      prenom: args.parent?.prenom ?? '',
      nom: args.parent?.nom ?? '',
      telephone: texte(args.parent?.telephone),
      telephone2: texte(args.parent?.telephone2),
    },
    depart: {
      partSeul: d?.leaves_alone ?? null,
      heure: texte(d?.departure_time),
      personnes: personnesDuDepart(d, t.personnes).map((p) => ({
        prenom: p.first_name ?? '',
        nom: p.last_name ?? '',
        telephone: texte(p.phone_number),
        lien: texte(p.relationship),
      })),
    },
    sante: {
      allergies: a?.has_allergies ?? null,
      allergiesDetails: texte(a?.allergies_details),
      allergiesConsequences: texte(a?.allergies_consequences),
      regime: a?.special_diet ?? null,
      regimeDetails: texte(a?.diet_details),
      medicaments: s?.medication ?? null,
      medicamentsDetails: texte(s?.medication_details),
      medicamentsAutonomie: s?.medication_autonomy ?? null,
      aSavoir: texte(s?.specific_medical),
      antecedents: texte(s?.past_medical),
      tetanos: s?.tetanus ?? null,
      medecinNom: texte(s?.doctor_name),
      medecinTelephone: texte(s?.doctor_phone),
    },
    activites: {
      participe: ac?.can_participate ?? null,
      restrictions: texte(ac?.restriction_details),
      nage: ac?.can_swim ?? null,
      niveauNage: texte(ac?.swim_level),
      peurEau: ac?.water_fear ?? null,
      autres: texte(ac?.other_info),
    },
    inclusion: inc?.has_needs
      ? {
          situation: texte(inc.situation_details),
          impact: texte(inc.impact_details),
          accompagnement: texte(inc.needs_dedicated_staff),
          accompagnementDetails: texte(inc.staff_details),
          strategies: texte(inc.strategies),
          aides: texte(inc.assistive_devices),
          signauxStress: texte(inc.stress_signals),
          forces: texte(inc.strengths),
          experience: texte(inc.previous_experience),
        }
      : null,
    image: img
      ? {
          espacesInternes: img.internal_spaces === true,
          rapportsActivite: img.activity_reports === true,
          sitePublic: img.public_website === true,
          reseauxSociaux: img.social_media === true,
          imprimes: img.print_materials === true,
          le: texte(img.consented_at),
        }
      : null,
  };
}

/* ------------------------------------------------------------------------ */
/*  Le règlement                                                             */
/* ------------------------------------------------------------------------ */

/**
 * La couleur de la barre au bord gauche d'une ligne selon le règlement
 * (décidé le 25/10/2026 : « juste la ligne en couleur verte quand payé »,
 * plutôt que la pastille). Vert : payé. Ambre : il reste à payer. Rien : pas
 * de facture, crédité, annulé, illisible.
 *
 * Une barre dessinée à part, pas une bordure : `divide-y` de la liste pose sa
 * propre couleur de bordure sur chaque ligne sauf la première, avec une
 * spécificité qui l'emportait — seule la première ligne était verte
 * (vu le 25/10/2026).
 */
export function couleurDeReglement(cle: CleDeReglement): string {
  if (cle === 'paid') return 'bg-green-500';
  if (cle === 'unpaid' || cle === 'partial') return 'bg-amber-400';
  return '';
}
